import type { FieldConfiguration } from "@/types/workflow"
import { ColumnTypeStruct, CustomFieldInputTypeStruct } from "@/types/workflow"

/**
 * Maps a custom-field INPUT type (what the user picks) to the physical COLUMN type (the slot family
 * the value is stored in). Must stay in lock-step with the backend's
 * `WorkflowHubV2Service.MapCustomFieldInputTypeToColumnType`, because the server allocates the slot:
 * if the two disagree, the UI offers an input type whose slot family is exhausted (create fails) or
 * hides one whose slots are free.
 *
 * Shared so the "Edit columns" modal and the View Designer's field panel can't drift apart.
 *
 * Note `ColumnTypeStruct.Date` is the string "DateTime", which is what the server uses for Date.
 */
export function mapCustomFieldInputTypeToColumnType(inputType: string, allowMultipleSelections: boolean): string {
  switch (inputType) {
    case CustomFieldInputTypeStruct.Text:
    case CustomFieldInputTypeStruct.Email:
      return ColumnTypeStruct.Text
    // Yes/No occupies a BOOLEAN slot server-side. This previously returned Text here, so the
    // availability check consulted the wrong slot family entirely.
    case CustomFieldInputTypeStruct.YesNo:
      return ColumnTypeStruct.Boolean
    case CustomFieldInputTypeStruct.Dropdown:
      return allowMultipleSelections ? ColumnTypeStruct.TextList : ColumnTypeStruct.Text
    case CustomFieldInputTypeStruct.Date:
      return ColumnTypeStruct.Date
    case CustomFieldInputTypeStruct.Number:
      return ColumnTypeStruct.Double
    case CustomFieldInputTypeStruct.Checklist:
      return ColumnTypeStruct.Checklist
    default:
      throw new Error("Not implemented")
  }
}

/**
 * Last-resort input type for a custom field whose `customFieldInputTypeCode` was never set — derived
 * from the column's storage type rather than defaulting to Dropdown.
 *
 * Defaulting to "Dropdown" made a Double column ("Number field", ColumnCode DoubleField2,
 * CustomFieldInputTypeCode null) open as a dropdown complete with Option rows, inviting an admin to
 * save dropdown options onto a numeric column. Any custom field with a null input-type code was
 * misreported the same way; the fallback simply never consulted columnTypeCode.
 *
 * Dropdown remains the fallback for genuinely option-shaped columns (Text/String slots carrying
 * CustomValues), which is what the original default was reaching for.
 *
 * Shared so the "Edit columns" modal and the View Designer's field panel resolve the SAME input type
 * for the same field. They had drifted: the modal consulted this fallback and the panel did not, so a
 * Boolean column with a null input-type code was a locked-down Yes/No field in one editor and a fully
 * editable dropdown in the other — which is how boolean fields ended up with renamed and extra
 * options in the first place.
 */
export function inputTypeFromColumnType(columnTypeCode?: string | null): string {
  switch ((columnTypeCode || "").toLowerCase()) {
    case "double":
    case "decimal":
    case "int":
    case "integer":
    case "number":
    // Money is numeric, not option-shaped. Omitting it left the exact bug this function fixes live on
    // currency columns — and mapCustomFieldInputTypeToColumnType has no Money branch either, so a save
    // from a wrongly-shown Dropdown would resolve the slot family as Text/TextList rather than the
    // Money slot the value actually lives in.
    case "money":
      return CustomFieldInputTypeStruct.Number
    case "email":
      return CustomFieldInputTypeStruct.Email
    case "boolean":
    case "bool":
      return CustomFieldInputTypeStruct.YesNo
    case "date":
    case "datetime":
    case "datetimeoffset":
      return CustomFieldInputTypeStruct.Date
    case "checklist":
      return CustomFieldInputTypeStruct.Checklist
    default:
      return CustomFieldInputTypeStruct.Dropdown
  }
}

/**
 * The input type an EXISTING field should be edited as: its explicit `customFieldInputTypeCode` when
 * the field carries one, otherwise derived from the storage type. Use this rather than reading
 * `customFieldInputTypeCode` directly — a null code is common on older custom fields.
 */
export function resolveFieldInputType(field?: {
  customFieldInputTypeCode?: string | null
  customValuesType?: string | null
  columnTypeCode?: string | null
} | null): string {
  return field?.customFieldInputTypeCode
    || field?.customValuesType
    || inputTypeFromColumnType(field?.columnTypeCode)
}

// Helper to check if field is boolean
export function isBooleanField(field: FieldConfiguration) {
  return field.columnTypeCode?.toLowerCase() === 'boolean' || field.customValuesType?.toLowerCase() === 'yesno';
}

// Helper to check if field should be rendered as a dropdown. Three sources of
// dropdown options: (1) tenant-managed CustomValues array stored on the field
// config itself, (2) the special AssignedToUser column which routes through
// the user picker, (3) a CodeValueSource that points at a global code-values
// table (currencies, job titles, etc.). The bulk-edit modal previously only
// recognised (1) and (2); (3) fell through to a text input. That worked for
// WorkflowHubV2Report (where CodeValueSource fields are display-only) but
// broke VMS Requests where they're editable.
export function isDropdownField(field: FieldConfiguration) {
  return (
    (Array.isArray(field.customValues) && field.customValues.length > 0)
    || field.columnCode === 'AssignedToUser'
    || !!field.codeValueSource
  );
}

// Helper to check if field is textList
export function isTextListField(field: FieldConfiguration) {
  return field.columnTypeCode?.toLowerCase() === 'textlist';
}

// Columns that carry columnTypeCode 'Email' but are NOT an email string, so the email cell
// treatment (mailto, copy, format validation) must not claim them.
//
// AssignedToUser is the user picker: its value is an OBJECT ({ProfileId, Email, FullName, ...}),
// isDropdownField() already claims it, and it is editable on the request configs. Left in, a
// mailto icon would appear on a picker and Copy would emit "[object Object]". It is only typed
// Email on the pre-WorkOrder configs (Ats/Client/PreOnboard/Vms); WorkOrderV2 exposes the real
// string as the separate AssignedToUserEmail column, which IS an email field.
const NON_EMAIL_COLUMN_CODES = new Set(['assignedtouser']);

/**
 * True when a column should get the email cell treatment. This is an OR, not an AND, because the
 * two codes disagree in production and neither one alone finds every email column:
 *
 *   • System email columns carry `columnTypeCode = 'Email'` with a null `customFieldInputTypeCode`
 *     (WorkerEmail, ManagerEmail, RecruiterEmail, PrimaryJobOwnerEmail, PrimaryContactEmail,
 *     AssignedToUserEmail, VmsEmail's From/To).
 *   • Tenant-created custom email fields carry `customFieldInputTypeCode = 'Email'`, and their
 *     `columnTypeCode` is EITHER 'Text' or 'Email' depending on which backend write touched them
 *     last. Two conventions disagree: mapCustomFieldInputTypeToColumnType allocates Email into a
 *     TEXT slot, while CreateCustomFieldConfigurationAsync overrides that and stores 'Email'.
 *     Observed on one tenant: TextField30 is Text+Email, TextField43 and TextField11 are
 *     Email+Email.
 *
 * So `columnTypeCode === 'Email'` alone misses the Text-slotted ones, and
 * `customFieldInputTypeCode === 'Email'` alone misses the system columns. Requiring both would
 * match only the accidental overlap. Don't narrow this to one code without reconciling the two
 * backend conventions first.
 */
export function isEmailField(field: FieldConfiguration | any): boolean {
  if (!field) return false;
  if (NON_EMAIL_COLUMN_CODES.has(String(field.columnCode || '').toLowerCase())) return false;

  // customValuesType is the one that actually matters for grid columns. The column DTO RENAMES the
  // input type on the way out — `CustomValuesType = f.CustomFieldInputTypeCode` in
  // WorkflowHubV2Service (both projections) — so a grid column never carries
  // customFieldInputTypeCode at all. Checking only that name matched custom email fields whose
  // columnTypeCode happened to be 'Email' and silently missed the ones slotted as 'Text' (or with no
  // columnTypeCode, which the field-config update path leaves absent): no compose/copy menu, and no
  // format validation either, since editable-cell and bulk-edit both gate on this helper.
  //
  // customFieldInputTypeCode is kept because callers that pass a raw FieldConfiguration — the field
  // editor, and the tests — use that name.
  const inputType = String(field.customValuesType || field.customFieldInputTypeCode || '').toLowerCase();

  return field.columnTypeCode?.toLowerCase() === 'email' || inputType === 'email';
}

/**
 * True when `value` satisfies the column's `fieldFormatting` regex, or when the check does not apply.
 *
 * Shared by the inline path (editable-cell) and the bulk path (bulk-edit-modal) so the two cannot
 * disagree about what is valid. They did: bulk edit had no format check at all, so a value the grid
 * rejected inline could still be written to every selected row.
 *
 * Three cases deliberately skip validation:
 *   • No regex, or an empty value — clearing a field is not a format error.
 *   • Date / DateTime columns. There `fieldFormatting` is a DISPLAY HINT ("ShortDate", "DateTime"),
 *     not a pattern; `new RegExp("ShortDate").test("2026-05-27")` never matches, so treating it as one
 *     failed every date edit. Detected with `isDateField`, which also accepts
 *     `customValuesType === 'date'` — a config edited through the field-config UPDATE path can have no
 *     `columnTypeCode` at all, and keying on that alone let those columns fall through to the regex.
 *   • `codeValueSource` columns, where the user picks from a constrained list, so any value arriving
 *     here is already a valid code.
 *
 * An unparseable regex passes rather than blocking — a malformed config should not make a column
 * uneditable.
 */
export function matchesFieldFormatting(field: FieldConfiguration | any, value: any): boolean {
  if (!field?.fieldFormatting || !value) return true;

  if (isDateField(field)) return true;
  if (field.codeValueSource) return true;

  try {
    return new RegExp(field.fieldFormatting).test(String(value));
  } catch {
    console.warn('Invalid regex pattern in fieldFormatting:', field.fieldFormatting);
    return true;
  }
}

// Helper to check if field is date or datetime
export function isDateField(field: FieldConfiguration) {
  return field.columnTypeCode?.toLowerCase() === 'date' || field.columnTypeCode?.toLowerCase() === 'datetime' || field.customValuesType?.toLowerCase() === 'date';
}

// Helper to check if a date field should also expose a TIME picker.
//
// `fieldFormatting` is the authoritative display intent — it's the radio admins
// flip in the field editor and the value `grid-cell.tsx` already keys on for
// the read-only renderer. So if it's set, we honor it verbatim:
//   - fieldFormatting === 'DateTime'  → show time picker
//   - fieldFormatting === 'ShortDate' → date-only even if columnTypeCode is
//     'DateTime' (the column might carry timestamps under the hood, but the
//     admin has opted to surface only the date portion to operators)
//
// When `fieldFormatting` is absent we fall back to the column's intrinsic type:
//   - columnTypeCode === 'datetime' → show time picker
//   - anything else                 → date-only
//
// All values land at the BE as ISO 8601 strings either way — the difference is
// only whether the time portion is meaningful or always 00:00:00. The showTime
// path emits a full ISO 8601 UTC string; the date-only path keeps emitting
// "YYYY-MM-DDT00:00:00.000Z" via formatDateForSave in editable-cell.tsx.
export function isDateTimeField(field: FieldConfiguration) {
  if (!isDateField(field)) return false;
  // Match grid-cell.tsx's render-side logic exactly: a missing fieldFormatting
  // is treated as 'ShortDate' (date-only), so the editor's picker stays in
  // lockstep with what the row will display after save. If we ever diverged,
  // the user could set a time, save it, and see only the date back — that's a
  // surprise we want to avoid.
  return field.fieldFormatting?.toLowerCase() === 'datetime';
}

// Helper to check if field is number, decimal, or money
export function isNumberField(field: FieldConfiguration) {
  const type = field.columnTypeCode?.toLowerCase();
  return type === 'number' || type === 'double' || type === 'decimal' || type === 'money';
}

// Helper to check if field is searchable
export function isSearchableField(field: FieldConfiguration) {
  return field.isSearchable === true;
}

// Helper to check if field is facetable
export function isFacetableField(field: FieldConfiguration) {
  return field.isFacetable === true;
}

// Helper to check if field is filterable
export function isFilterableField(field: FieldConfiguration) {
  return field.isFilterable === true;
}

// Helper to check if field has CodeValueSource
export function hasCodeValueSource(field: FieldConfiguration) {
  return !!field.codeValueSource;
}

// Helper to check if field is a collection (array of objects)
export function isCollectionField(field: FieldConfiguration): boolean {
  return field.columnTypeCode?.toLowerCase() === 'collection';
}

// Helper to check if collection field needs OData /any syntax for filtering
export function requiresCollectionFilter(field: FieldConfiguration): boolean {
  return isCollectionField(field) && !!field.subColumnName;
}

// Helper to get the search column path (handles subColumnName)
export function getSearchColumnPath(column: FieldConfiguration | any): string {
  return column.subColumnName 
    ? `${column.columnCode}/${column.subColumnName}` 
    : column.columnCode;
}

// Helper to get facet keys from global filter configuration (with subColumnName paths)
export function getFacetKeysFromConfiguration(configuration: any): string[] {
  if (!configuration?.globalFilterIds || !configuration?.fieldsInUse) return [];
  
  return configuration.globalFilterIds
    .map((fid: string) => {
      const col = configuration.fieldsInUse.find((c: any) => c.fieldConfigurationId === fid);
      if (!col) return null;
      return getSearchColumnPath(col);
    })
    .filter((x: any): x is string => typeof x === 'string');
}

// Helper to get select fields from global filter configuration (just columnCode, no subColumnName)
export function getSelectFieldsFromConfiguration(configuration: any): string[] {
  if (!configuration?.globalFilterIds || !configuration?.fieldsInUse) return [];
  
  return configuration.globalFilterIds
    .map((fid: string) => {
      const col = configuration.fieldsInUse.find((c: any) => c.fieldConfigurationId === fid);
      if (!col) return null;
      return col.columnCode;
    })
    .filter((x: any): x is string => typeof x === 'string');
}

// Helper to build a search.ismatch filter for searchable fields that supports multi-word input.
// Each word gets its own regex match and they are combined with 'and' so all words must match.
//
// Dashes are treated as word separators, same as whitespace. The index-side analyzer
// (standard Lucene) tokenizes "ABC-123" into the terms "abc" and "123" — no single
// indexed term ever contains the dash, so a regex like /.*abc-123.*/ can never match
// and the search silently returns nothing. Splitting the input on dashes and requiring
// every part reproduces exactly how the value is actually stored in the index. This is
// a tokenization problem, not an escaping one — encoding the dash cannot fix it.
export function buildSearchableFilter(searchText: string, searchColumn: string): string {
  const words = searchText.trim().split(/[\s-]+/).filter(Boolean);
  if (words.length === 0) return '';

  const conditions = words.map(word => {
    // Escape regex metacharacters so user input is treated literally
    const regexEscaped = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Escape single quotes for the OData string
    const odataEscaped = regexEscaped.replace(/'/g, "''");
    return `search.ismatch('/.*${odataEscaped}.*/', '${searchColumn}', 'full', 'any')`;
  });

  return conditions.length === 1 ? conditions[0] : conditions.join(' and ');
}

// THE canonical builder for checkbox-style value filters (facets, custom values,
// boolean pickers) — shared by the grid column popovers, the sidebar filter builder,
// the filter drawer, and the view designer so all four produce byte-identical OData
// for the same selection. `values` may contain the sentinels '__SELECT_ALL__' and
// '__BLANK__' alongside regular values.
//
// Type-awareness matters because the index fields are strongly typed:
//   • boolean → values UNQUOTED lowercase (`eq true`); blank is `eq null` ONLY —
//     `eq ''` against Edm.Boolean is an invalid comparison and the whole request 400s
//     (the view designer used to emit `(BooleanField3 eq null or BooleanField3 eq '')
//     or (BooleanField3 eq 'true')` — every part of it broken).
//   • number / date → values unquoted; blank is `eq null` only (Edm.Double /
//     Edm.DateTimeOffset have the same no-'' rule).
//   • text → values quoted; blank is `eq null or eq ''` (legacy rows carry either).
//   • textlist / collection → `/any()` emptiness checks, values always quoted
//     (the sub-items are strings regardless of the parent's type code).
//
// Returns the bare filter body — callers add their own scope prefix (e.g. the global
// filters' "(Stage ne 'Discarded' and IsDeleted eq false)") and persist their own
// {regularValues, hasBlanks} metadata for pill/popover rehydration.
export function buildFacetedValuesFilter(field: FieldConfiguration | any, values: string[]): string {
  const hasSelectAll = values.includes('__SELECT_ALL__');
  const hasBlank = values.includes('__BLANK__');
  const regularValues = values.filter(v => v !== '__SELECT_ALL__' && v !== '__BLANK__');
  const columnPath = getSearchColumnPath(field);
  const isCollectionWithSub = isCollectionField(field) && !!field.subColumnName;
  const isListLike = isTextListField(field) || isCollectionWithSub;
  const isUnquoted = isBooleanField(field) || isNumberField(field) || isDateField(field);

  const valueExpr = (v: string) =>
    isBooleanField(field) ? v.toLowerCase()
      : isUnquoted ? v
        : `'${v.replace(/'/g, "''")}'`;

  if (hasSelectAll) {
    // "Select all" = any non-empty value.
    if (isListLike) return `${field.columnCode}/any()`;
    if (isUnquoted) return `${columnPath} ne null`;
    return `${columnPath} ne null and ${columnPath} ne ''`;
  }

  if (isListLike) {
    const itemPath = isCollectionWithSub ? `x/${field.subColumnName}` : 'x';
    const orConditions = regularValues.map(v => `${itemPath} eq '${v.replace(/'/g, "''")}'`).join(' or ');
    if (hasBlank && regularValues.length > 0) return `(not ${field.columnCode}/any() or ${field.columnCode}/any(x: ${orConditions}))`;
    if (hasBlank) return `not ${field.columnCode}/any()`;
    if (regularValues.length > 0) return `${field.columnCode}/any(x: ${orConditions})`;
    return '';
  }

  const blankCondition = isUnquoted
    ? `${columnPath} eq null`
    : `${columnPath} eq null or ${columnPath} eq ''`;
  const orConditions = regularValues.map(v => `${columnPath} eq ${valueExpr(v)}`).join(' or ');

  if (hasBlank && regularValues.length > 0) return `((${blankCondition}) or (${orConditions}))`;
  if (hasBlank) return `(${blankCondition})`;
  if (regularValues.length > 0) return regularValues.length > 1 ? `(${orConditions})` : orConditions;
  return '';
}

// Build a stage-filter clause + pill metadata for a single field, typed by the
// field's configuration. Shared by the worker-search modal's WO row click and
// the cross-tab request handoff (workflow-grid) so both produce identical
// filters. The `values` metadata uses the same JSON shape the column popovers
// and view designer persist ({search} / {regularValues} / {start,end}) so the
// applied-filter pills can render a value preview for these filters.
export function buildFilterForField(fieldConfig: any, value: any): { filter: string; values: any } | null {
  if (!value) return null;

  let filterString = '';
  let filterValue: any = value;

  // Build filter based on field type
  if (isSearchableField(fieldConfig)) {
    // For searchable fields, use search.ismatch with exact match (like selected suggestions)
    const searchColumn = getSearchColumnPath(fieldConfig);
    const escapedValue = String(value).replace(/'/g, "''");
    filterString = `search.ismatch('${escapedValue}', '${searchColumn}', 'full', 'all')`;
    filterValue = { search: String(value) };
  } else if (isDateField(fieldConfig)) {
    // For date fields, handle StartDate and EndDate separately
    // Parse the date value (could be ISO string or Date object)
    let dateValue: Date;
    if (value instanceof Date) {
      dateValue = value;
    } else if (typeof value === 'string') {
      dateValue = new Date(value);
    } else {
      return null; // Skip if value is not a valid date format
    }

    if (!isNaN(dateValue.getTime())) {
      // Format as YYYY-MM-DD
      const year = dateValue.getUTCFullYear();
      const month = String(dateValue.getUTCMonth() + 1).padStart(2, '0');
      const day = String(dateValue.getUTCDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;

      // Build filter: date ge startOfDay and date lt startOfNextDay
      const startStr = `${dateStr}T00:00:00Z`;
      const nextDay = new Date(Date.UTC(year, dateValue.getUTCMonth(), dateValue.getUTCDate() + 1));
      const nextDayYear = nextDay.getUTCFullYear();
      const nextDayMonth = String(nextDay.getUTCMonth() + 1).padStart(2, '0');
      const nextDayDay = String(nextDay.getUTCDate()).padStart(2, '0');
      const nextDayStr = `${nextDayYear}-${nextDayMonth}-${nextDayDay}T00:00:00Z`;

      filterString = `${fieldConfig.columnCode} ge ${startStr} and ${fieldConfig.columnCode} lt ${nextDayStr}`;
      filterValue = { start: dateStr, end: null };
    }
  } else if (isNumberField(fieldConfig)) {
    // For number fields, use direct equality
    filterString = `${fieldConfig.columnCode} eq ${value}`;
    filterValue = { regularValues: [String(value)] };
  } else {
    // For regular text fields, use equality
    const escapedValue = String(value).replace(/'/g, "''");
    filterString = `${fieldConfig.columnCode} eq '${escapedValue}'`;
    filterValue = { regularValues: [String(value)] };
  }

  return filterString ? { filter: filterString, values: filterValue } : null;
}

// Helper to get display value for columns with subColumnName (extracts property from objects)
export function getDisplayValueFromColumn(value: any, column: FieldConfiguration | any): string {
  // If column has subColumnName and value is an object, extract that property
  if (column.subColumnName && typeof value === 'object' && value !== null) {
    // Try different case variations of the property name
    const subColLower = column.subColumnName.toLowerCase();
    const foundKey = Object.keys(value).find(k => k.toLowerCase() === subColLower);
    if (foundKey && value[foundKey] !== undefined && value[foundKey] !== null) {
      return String(value[foundKey]);
    }
    // Fallback: try direct access
    if (value[column.subColumnName] !== undefined && value[column.subColumnName] !== null) {
      return String(value[column.subColumnName]);
    }
    // subColumnName configured but property not found in object — show empty cell
    return "";
  }
  // Objects without a subColumnName should not be serialised into the cell
  if (typeof value === 'object' && value !== null) {
    return "";
  }
  return value ?? "";
}

// Helper to build OData collection filter using /any syntax
export function buildCollectionFilter(
  columnCode: string,
  subColumnName: string,
  selectedValues: string[]
): string {
  if (selectedValues.length === 0) return '';
  
  // Build /any conditions for each selected value
  const conditions = selectedValues.map(value => 
    `${columnCode}/any(x: x/${subColumnName} eq '${value.replace(/'/g, "''")}')`
  );
  
  return `(${conditions.join(' or ')})`;
}

// Helper to extract display values from collection with translation support
export function getCollectionDisplayValue(
  value: any,
  column: FieldConfiguration,
  fieldInteraction?: any,
  language: string = 'en'
): { items: any[]; displayText: string } {
  const items = Array.isArray(value) ? value.filter((item: any) => !item?.IsDeleted && !item?.isDeleted) : [];
  
  if (items.length === 0) {
    return { items: [], displayText: '' };
  }
  
  // Extract subColumn values and translate if possible
  const displayValues = items.map(item => {
    // Extract code with case-insensitive property access
    let code: string;
    if (column.subColumnName && typeof item === 'object' && item !== null) {
      const subColLower = column.subColumnName.toLowerCase();
      const foundKey = Object.keys(item).find(k => k.toLowerCase() === subColLower);
      code = foundKey ? String(item[foundKey]) : String(item);
    } else {
      code = String(item);
    }
    
    // Try to find translation in fieldInteraction
    if (fieldInteraction?.customValues) {
      const cv = fieldInteraction.customValues.find((v: any) => {
        const cvCode = v.code || v.Code;
        return String(cvCode).toLowerCase() === String(code).toLowerCase();
      });
      
      if (cv) {
        const titleFr = cv.titleFrench || cv.TitleFrench;
        const titleEn = cv.titleEnglish || cv.TitleEnglish;
        return language === 'fr' 
          ? (titleFr || titleEn || code)
          : (titleEn || code);
      }
    }
    
    return code;
  });
  
  return {
    items,
    displayText: displayValues.join(', ')
  };
} 